import { useDynamicModal } from 'components/DynamicModalProvider/useDynamicModal';
import SimpleMenu from 'components/SimpleMenu';
import { useCallback, useMemo } from 'react';
import { SimpleMenuItem } from 'types';
import { ItemFilter, filterItemWithKeywords } from 'FiltersModule/utils';
import { useTenants } from './useTenants';
import { Tenant } from 'TenantModule/types';
import { makeTenantLabel } from 'TenantModule/utils';
import { useStateEffect } from 'hooks/useStateEffect';
import { TenantStatusTypes, onboardingTenantStatuses } from 'TenantModule/constants';
import useSandboxes from 'SandboxModule/hooks/useSandboxes';

type TenantMenuItem = SimpleMenuItem & {
  name: Tenant['tenantName'];
  value: Tenant['id'];
};

const itemFilter: ItemFilter = ({ searchWords, item }) =>
  filterItemWithKeywords({
    searchWords,
    item,
    filterKeysArray: ['name'],
  });

/**
 * @param allowOnboardingTenants Management-form mode (GLX-1827): tenants still in
 *   Creating / Awaiting Infrastructure / Creation Failed stay selectable, and the
 *   has-realms requirement is dropped — the management forms (secret mappings,
 *   service registration) are exactly the setup work that moves a tenant toward
 *   Ready, before it has realms. The default (context switching) stays Ready-only.
 */
export const useTenantDropdown = ({
  allowOnboardingTenants = false,
}: { allowOnboardingTenants?: boolean } = {}) => {
  const { tenantOrder, selectedGlobalTenant, getTenant, defaultTenant, handleSelectGlobalTenant } =
    useTenants();

  const [selectedTenant, setSelectedLocalTenant] = useStateEffect<Tenant | undefined>(
    selectedGlobalTenant ?? defaultTenant,
  );

  const { getSandbox } = useSandboxes();

  const tenantMenuItems = useMemo<TenantMenuItem[]>(() => {
    if (defaultTenant) {
      const items: TenantMenuItem[] = [
        {
          label: makeTenantLabel({
            name: `${defaultTenant.tenantName} (Default)`,
          }),
          value: defaultTenant.id,
          name: defaultTenant.tenantName,
        },
      ];

      tenantOrder.forEach((tenantId: number) => {
        const tenant = getTenant({ tenantId });
        const isDefault = tenant?.isDefault;
        const isTenantArchived = tenant?.isArchived;
        const realms = tenant?.realms;
        let noAvailableRealms = false;

        if (realms) {
          noAvailableRealms = realms.every((realmId) => {
            const realm = getSandbox(realmId);

            return realm && !!realm.archived_at;
          });
        }

        if (tenant && !isDefault && !isTenantArchived) {
          const statusAllowed =
            tenant.status === TenantStatusTypes.Ready ||
            (allowOnboardingTenants && onboardingTenantStatuses.has(tenant.status));
          const realmsRequired = !allowOnboardingTenants;
          const missingRealms = noAvailableRealms || !tenant?.realms || tenant.realms.length === 0;
          const { tenantTooltip, isTenantDisabled } =
            !statusAllowed || (realmsRequired && missingRealms)
              ? {
                  tenantTooltip: !statusAllowed ? 'Tenant is not ready' : 'No available realms',
                  isTenantDisabled: true,
                }
              : {
                  tenantTooltip: undefined,
                  isTenantDisabled: false,
                };
          const tenantName = tenant.tenantName;

          items.push({
            label: makeTenantLabel({
              name: tenantName,
            }),
            value: tenantId,
            name: tenantName,
            disableButton: isTenantDisabled,
            tooltip: tenantTooltip,
          });
        }
      });

      return items;
    }

    return [];
  }, [allowOnboardingTenants, defaultTenant, getSandbox, getTenant, tenantOrder]);

  const { openModal } = useDynamicModal();

  const openTenantDropdown = useCallback(
    ({
      target,
      onSelect,
      shouldSkipSetTenant = false,
    }: {
      target: HTMLElement;
      onSelect?: (args: { tenant: Tenant }) => void;
      shouldSkipSetTenant?: boolean;
    }) => {
      const onClick = ({ item }: { item: TenantMenuItem }) => {
        const tenant = getTenant({ tenantId: item.value });

        if (tenant) {
          onSelect?.({ tenant });
          if (!shouldSkipSetTenant) {
            setSelectedLocalTenant(tenant);
          }
        }
      };

      openModal({
        modal: ({ isOpen: _isOpen, toggle }) => {
          return (
            <SimpleMenu<TenantMenuItem>
              anchorEl={target}
              items={tenantMenuItems}
              handleClickV2={onClick}
              handleClose={toggle}
              itemFilter={itemFilter}
              showSearchBar
              transformMenu="translateX(0px) translateY(0px)"
              matchAnchorWidth
              noMarginTop
              menuMaxHeight={316}
            />
          );
        },
      });
    },
    [openModal, getTenant, setSelectedLocalTenant, tenantMenuItems],
  );

  return {
    openTenantDropdown,
    makeTenantLabel,
    selectedTenant,
    selectedGlobalTenant,
    setSelectedLocalTenant,
  };
};
